
































import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  useRoute,
  watch,
} from '@nuxtjs/composition-api';
import kebabCase from 'lodash.kebabcase';

import AnchorLinkedBlock from './AnchorLinkedBlock.vue';
export interface IAnchorLinkedBlock {
  blockTitle: string;
  blockContent: {
    componentName: string;
    props?: Record<string, unknown>;
  };
}

export default defineComponent({
  name: 'AnchorLinkedBlocks',
  components: { AnchorLinkedBlock },
  props: {
    blocks: {
      type: Array as PropType<IAnchorLinkedBlock[]>,
      required: true,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props) {
    const route = useRoute();
    const visibilityList = ref(Array(props.blocks.length).fill('out-of-view'));
    const windowTop = ref(0);

    const onScroll = () => {
      windowTop.value = window.scrollY;
    };

    onMounted(() => {
      window.addEventListener('scroll', onScroll);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', onScroll);
    });

    const topBuffer = 100;
    const atTopOfPage = computed(() => windowTop.value < topBuffer);

    const highlightedBlockIndex = computed(() => {
      let index = visibilityList.value.findIndex((visibility) => visibility === 'in-view');
      if (atTopOfPage.value) {
        index = -1;
      }
      return index;
    });

    const setHashInURL = (hash: string) => {
      if (process.browser && window) {
        window.history.replaceState(null, '', `#${hash}`);
      }
    };

    const clearHashInURL = () => {
      if (process.browser && window) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    };

    watch(
      () => highlightedBlockIndex.value,
      (index) => {
        if (index !== -1 && !atTopOfPage.value) {
          setHashInURL(kebabCase(props.blocks[index].blockTitle));
        } else {
          clearHashInURL();
        }
      },
    );

    const handleIntersections = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        const numericId = parseInt(entry.target.id, 10);
        if (entry.isIntersecting) {
          // vue cannot detect direct index setting, need to use splice. see: https://v2.vuejs.org/v2/guide/reactivity.html#For-Arrays:~:text=Vue%20cannot%20detect%20the%20following%20changes%20to%20an%20array%3A
          visibilityList.value.splice(numericId, 1, 'in-view');
        } else {
          visibilityList.value.splice(numericId, 1, 'out-of-view');
        }
      }
    };

    const observer = ref<any | null>(null);
    const createIntersectionObserver = () => {
      let observerOptions = {
        rootMargin: '-20% 0px -20% 0px',
        threshold: 0,
      };
      observer.value = new IntersectionObserver(handleIntersections, observerOptions);
    };
    onMounted(createIntersectionObserver);

    const scrollToId = (id: string) => {
      window.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    onMounted(() => {
      const hash = route.value.hash;
      if (hash) {
        const blockTitleId = hash.replace('#', '');
        scrollToId(blockTitleId);
      }
    });

    return { observer, scrollToId, visibilityList, kebabCase, highlightedBlockIndex };
  },
});
